// The station sheet's Wake-on-LAN section (F-STN-17, spec/13 §3.3): whether Wake from the console will switch this
// PC on. "Ready" when the station reported Fast Startup off and a wired adapter armed end to end; "Needs attention"
// with each reason in words otherwise; "Not reported" for a station running an older app. Beneath it, each wired
// adapter and the addresses a wake sends the magic packet to — the BIOS still has to allow it, and the sheet says so.

import type { FloorStationState } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { useCurrentVenue } from '../shell/scope';
import { Section } from '../ui';
import { useStationWakeStatus } from './queries';
import { useProblem } from '../api/use-problem';

export function WakeReadiness({ station }: { readonly station: FloorStationState }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const status = useStationWakeStatus(venue.organizationId, venue.id, station.stationId, true);
  const data = status.data;
  const readiness = data?.readiness ?? null;

  let badge: ReactNode = null;
  let body: ReactNode;
  if (status.isError) {
    body = <p className="console-muted">{problem.text(status.error, { fallback: t('floor.drawer.wake.failed') })}</p>;
  } else if (data === undefined) {
    body = <p className="console-muted">{t('floor.drawer.wake.loading')}</p>;
  } else {
    badge =
      readiness === null ? (
        <Badge tone="neutral">{t('floor.drawer.wake.unknown')}</Badge>
      ) : readiness.ready ? (
        <Badge tone="ok">{t('floor.drawer.wake.ready')}</Badge>
      ) : (
        <Badge tone="warn">{t('floor.drawer.wake.needsAttention')}</Badge>
      );
    body = (
      <>
        {readiness === null ? (
          <p className="console-muted">{t('floor.drawer.wake.neverReported')}</p>
        ) : (
          <>
            {readiness.issues.length > 0 ? (
              <ul className="console-station-sheet__wake-issues">
                {readiness.issues.map((issue) => (
                  <li key={issue}>{t(`floor.drawer.wake.issue.${issue}`)}</li>
                ))}
              </ul>
            ) : null}
            {readiness.adapters.length > 0 ? (
              <ul className="console-station-sheet__titles" aria-label={t('floor.drawer.wake.title')}>
                {readiness.adapters.map((adapter) => (
                  <li key={adapter.mac} className={adapter.ready ? undefined : 'console-muted'}>
                    <span className="console-num">
                      {t(adapter.ready ? 'floor.drawer.wake.adapterReady' : 'floor.drawer.wake.adapterNotReady', { name: adapter.name, mac: adapter.mac })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="console-muted">{t('floor.drawer.wake.checkedAt', { time: new Date(readiness.checkedAt).toLocaleString() })}</p>
          </>
        )}
        <p className="console-muted">
          {data.macAddresses.length === 0 ? t('floor.drawer.wake.noAddress') : t('floor.drawer.wake.addresses', { macs: data.macAddresses.join(', ') })}
        </p>
      </>
    );
  }

  return (
    <Section title={t('floor.drawer.wake.title')} description={t('floor.drawer.wake.hint')} titleAs="h3" actions={badge ?? undefined}>
      {body}
    </Section>
  );
}
